import React, { useMemo, useRef } from 'react';
import classNames from 'classnames';
import { LayoutGrid } from 'lucide-react';

import { useIntl } from '../../util/reactIntl';
import { buildSearchTarget } from '../../util/unifiedSearch';

import { useListingCounts } from './useListingCount';

import css from './SpaceTypeOptions.module.css';

const ALL = 'all';

/**
 * The four space-type options of the editor: Alle · Arbeitsplatz · Meetingraum · Büro (spec §4.1).
 *
 * Single select as an ARIA radio group: one tab stop (the checked option), arrow keys move the
 * selection, Enter/Space/click pick it. An option without stock at the draft's WHERE is hidden —
 * never the selected one, and never "Alle"; while the counts load every option shows, so nothing
 * disappears that was never known to be empty. The number itself is not printed.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id id prefix
 * @param {Array} props.groups resolved space-type groups
 * @param {string|null} props.value selected group key, null = all
 * @param {Function} props.onChange (key|null, { pick: boolean }) — pick = click/Enter/Space, an
 *   arrow key only moves the selection
 * @param {Object|null} [props.where] the draft's WHERE (for the stock check)
 * @param {Object} [props.current] readSearchState of the page
 * @param {boolean} [props.countsEnabled] whether the stock check may query
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const SpaceTypeOptions = props => {
  const {
    id,
    groups = [],
    value,
    onChange,
    where = null,
    current = {},
    countsEnabled = false,
    className,
  } = props;
  const intl = useIntl();
  const optionRefs = useRef({});

  const targets = useMemo(
    () =>
      groups.map(group => ({
        key: group.key,
        url: buildSearchTarget({ draft: { where, spaceType: group.key }, current, groups }),
      })),
    [groups, where, current]
  );
  const counts = useListingCounts(targets.map(t => t.url), { enabled: countsEnabled });

  const options = [
    { key: ALL, Icon: LayoutGrid },
    ...groups
      .filter(group => {
        const target = targets.find(t => t.key === group.key);
        return group.key === value || counts[(target?.url)] !== 0;
      })
      .map(group => ({ key: group.key, Icon: group.Icon })),
  ];
  const selectedKey = value || ALL;

  const select = (key, pick) => onChange(key === ALL ? null : key, { pick });

  const handleKeyDown = (event, index) => {
    const step =
      event.key === 'ArrowDown' || event.key === 'ArrowRight'
        ? 1
        : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
        ? -1
        : 0;
    if (step === 0) {
      return;
    }
    event.preventDefault();
    const next = options[(index + step + options.length) % options.length];
    select(next.key, false);
    optionRefs.current[next.key]?.focus();
  };

  return (
    <div
      className={classNames(css.root, className)}
      role="radiogroup"
      aria-label={intl.formatMessage({ id: 'UnifiedSearch.type.groupLabel' })}
      id={id}
    >
      {options.map(({ key, Icon }, index) => {
        const isSelected = key === selectedKey;
        return (
          <button
            key={key}
            ref={el => {
              optionRefs.current[key] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            className={classNames(css.option, { [css.optionSelected]: isSelected })}
            data-testid="unified-search-type-option"
            data-type={key}
            onClick={() => select(key, true)}
            onKeyDown={event => handleKeyDown(event, index)}
          >
            <span className={css.icon} aria-hidden="true">
              {Icon ? <Icon size={20} strokeWidth={1.75} /> : null}
            </span>
            <span className={css.text}>
              <span className={css.label}>
                {intl.formatMessage({ id: `UnifiedSearch.type.${key}` })}
              </span>
              <span className={css.hint}>
                {intl.formatMessage({ id: `UnifiedSearch.type.${key}Hint` })}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default SpaceTypeOptions;
